import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box, DialogContentText } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import qs from 'qs';

import CustomTable from 'components/common/customTableV2';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import Modal from 'components/common/modal';
import M from 'messages';
import { replacePath, getMessage } from 'helpers/helper';
import { useModal } from 'hooks/common';
import TasteimonialsService from 'services/tasteimonialsService';
import tasteimonialsStore from 'store/tasteimonialsStore';
import SidebarStore from 'store/ui/sidebarStore';
import domainData from 'store/domainStore';
import { adaptTasteimonialsTableData } from 'helpers/adapter';
import tableOptions from './config/config';
import CustomTableToolbar from './toolbar';
import ResponseModal from './components/ResponseModal';
import classes from './styles';

const Tasteimonials = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedRows, setSelectedRows] = useState([]);
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [loading, setLoading] = useState(true);
  const [tasteimonialsData, setTasteimonialsData] = useState({});
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [selectedTasteimonial, setSelectedTasteimonial] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isOpenDeleteModal, openDeleteModal, closeDeleteModal] = useModal(false);
  const isSidebarOpened = SidebarStore.sidebarState;
  const isUserSuperAdmin = domainData.userIsSuperAdmin();

  const setFilter = filter => {
    replacePath(navigate, filter);
    tasteimonialsStore.setFilter(filter);
  };

  const queryParams = tasteimonialsStore.getCurrentFilterParams(pageParams);

  const getTasteimonials = useCallback(
    async params => {
      try {
        setFilter(params);
        const response = await TasteimonialsService.getTasteimonials(params);
        if (response) {
          setTasteimonialsData({
            count: response.data?.count || 0,
            data: adaptTasteimonialsTableData(response.data?.data || []),
          });
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    getTasteimonials(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickDelete = () => {
    if (selectedRows.length === 0) {
      return;
    }
    openDeleteModal();
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      return;
    }
    try {
      setDeleteLoading(true);
      const response = await TasteimonialsService.removeMany(selectedRows);
      if (response?.data?.message) {
        enqueueSnackbar(response?.data?.message, { variant: 'success' });
      }
      setSelectedRows([]);
      getTasteimonials(queryParams);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    } finally {
      setDeleteLoading(false);
      closeDeleteModal();
    }
  };

  const handleResponseAction = row => {
    setSelectedTasteimonial(row);
    setResponseModalOpen(true);
  };

  const handleSaveResponse = async (id, response) => {
    await TasteimonialsService.updateResponse(id, response);
    getTasteimonials(queryParams);
  };

  const handleCloseResponseModal = () => {
    setResponseModalOpen(false);
    setSelectedTasteimonial(null);
  };

  const handleStatusChange = async status => {
    try {
      await TasteimonialsService.updateStatus({
        ids: selectedRows,
        status,
      });
      enqueueSnackbar(
        status === 'public' ? 'Tasteimonials made public successfully' : 'Tasteimonials made private successfully',
        { variant: 'success' },
      );
      getTasteimonials(queryParams);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    } finally {
      setSelectedRows([]);
    }
  };

  const { searchFields, filterFields } = tableOptions.tasteimonials;
  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getTasteimonials(newFilter);
    }
  };

  const onFilterCallback = (filterObj, reset = false) => {
    if (reset) {
      tasteimonialsStore.resetFilters();
      const newParams = tasteimonialsStore.getCurrentFilterParams();
      setLoading(true);
      getTasteimonials(newParams, false);
      return;
    }
    if (filterObj && !Array.isArray(filterObj)) {
      const fieldKeyList = Object.keys(filterObj);
      let newFilter = {};
      fieldKeyList.forEach(i => {
        const fieldValue = filterObj[i];
        let oldValue = queryParams.params?.filter?.[i];
        if (oldValue === undefined) {
          oldValue = '';
        }
        if (filterFields && oldValue !== fieldValue && fieldValue !== undefined) {
          newFilter = {
            ...newFilter,
            params: {
              ...queryParams.params,
              ...newFilter.params,
              filter: { ...queryParams.params.filter, ...newFilter?.params?.filter, [i]: fieldValue },
              skip: 0,
            },
          };
        }
      });
      if (Object.keys(newFilter).length) {
        setFilter(newFilter);
        getTasteimonials(newFilter);
      }
    }
  };

  const isMakePublicActionActive =
    selectedRows.length > 0 &&
    selectedRows.every(id => tasteimonialsData.data.find(row => row.id === id && row.status === 'private'));

  const isMakePrivateActionActive =
    selectedRows.length > 0 &&
    selectedRows.every(id => tasteimonialsData.data.find(row => row.id === id && row.status === 'public'));

  const toolbarView = (
    <CustomTableToolbar
      onSearchCallback={onSearchCallback}
      filterFields={filterFields}
      filteredParams={queryParams}
      setFilteredParams={setFilter}
      onFilterCallback={onFilterCallback}
      isNeedMakePublicAction={isMakePublicActionActive}
      isNeedMakePrivateAction={isMakePrivateActionActive}
      handleStatusChange={handleStatusChange}
      numSelected={selectedRows.length}
      handleDelete={isUserSuperAdmin ? handleClickDelete : null}
    />
  );

  if (loading) return <Loading />;

  return (
    <>
      <Box
        sx={{
          ...classes.root,
          ...(isSidebarOpened && classes.rootDrawerOpen),
          ...(!isSidebarOpened && classes.rootDrawerClose),
        }}>
        <PageHeader generalPageTitle={M.get('pages.tasteimonials')} onlyGeneralPageTitle />
        <Grid size={{ xs: 12 }}>
          <Box sx={classes.tableRoot}>
            <Grid container sx={classes.container}>
              <Box sx={classes.tabsContainer}>
                <CustomTable
                  tableSources={tasteimonialsData}
                  tableOptions={tableOptions.tasteimonials}
                  loading={loading}
                  filteredParams={queryParams}
                  setFilteredParams={setFilter}
                  getDataOnChange={getTasteimonials}
                  withOpenAction={false}
                  withEditAction={false}
                  withResponseAction
                  handleResponseAction={handleResponseAction}
                  toolbarView={toolbarView}
                  setSelectedRows={setSelectedRows}
                  selectedRows={selectedRows}
                  rowIsNotSelectable
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>
      <Modal
        open={isOpenDeleteModal}
        leftBtnText={M.get('actions.cancel')}
        rightBtnText={M.get('actions.delete')}
        handleClose={closeDeleteModal}
        handleSubmit={handleBulkDelete}
        title={M.get('tasteimonials.deleteModal.title')}
        submitLoading={deleteLoading}
        disableSave={deleteLoading}
        isDeleteModal={true}>
        <DialogContentText>
          {selectedRows.length === 1
            ? M.get('tasteimonials.deleteModal.description')
            : M.get('tasteimonials.deleteModal.descriptionPlural').replace('{count}', selectedRows.length)}
        </DialogContentText>
      </Modal>
      <ResponseModal
        open={responseModalOpen}
        onClose={handleCloseResponseModal}
        tasteimonial={selectedTasteimonial}
        onSave={handleSaveResponse}
      />
    </>
  );
});

export default Tasteimonials;
